import { DotSeparator } from '@frameio/shared/components/DotSeparator';
import { formatDuration } from '@frameio/shared/transfers/utils/formatDuration';
import { Text, theme } from '@frameio/vapor';
import { IconArrowDown, IconArrowUp } from '@frameio/vapor-icons';
import type { TransferBatch } from '../../../types/uploader';
import { getProgressType, getTransferBatchHasLocalData } from '../../../utils';
import { ARROW_ICON_CLASSNAME } from '../constants';
import { JobSize } from '../JobSize';
import { ScrollingJob } from '../ScrollingJob';
import TEXT from './generated/TEXT/InProgressJobStatus';

TEXT.DEFINE({
  /** Status label when downloading */
  downloading: 'Downloading',
  /** Status label when uploading */
  uploading: 'Uploading',
  /** Status label when transfer is paused */
  paused: 'Paused',
});

export const InProgressJobStatus = ({ job }: { job: TransferBatch }) => {
  const { isPaused, progress, batchType } = job;
  const hasLocalData = getTransferBatchHasLocalData(job);
  const progressType = getProgressType(job);
  const showJobSize = hasLocalData && progress?.remainingTime !== Infinity;
  const canShowRemainingTime = hasLocalData && !isPaused;

  return (
    <ScrollingJob>
      {batchType === 'DOWNLOAD' ? (
        <IconArrowDown
          className={ARROW_ICON_CLASSNAME}
          color={
            isPaused
              ? theme.colors.icon.secondary
              : theme.colors.icon.interactive.success
          }
          size={16}
        />
      ) : (
        <IconArrowUp
          className={ARROW_ICON_CLASSNAME}
          color={
            isPaused ? theme.colors.icon.secondary : theme.colors.brand.primary
          }
          size={16}
        />
      )}
      <>
        <Text
          color="body"
          data-testid="job-card--progress-status-text"
          fontWeight="medium"
          variant="secondary"
        >
          {isPaused
            ? TEXT.paused
            : `${
                progressType === 'DOWNLOAD' ? TEXT.downloading : TEXT.uploading
              }`}
        </Text>

        {showJobSize && (
          <div>
            <DotSeparator />
            <JobSize
              bytesSent={progress?.bytesSent ?? 0}
              bytesTotal={progress?.bytesTotal}
            />
          </div>
        )}
        {canShowRemainingTime && (
          <span className="remaining-time">
            <DotSeparator />
            <span>{formatDuration(progress?.remainingTime ?? 0)}</span>
          </span>
        )}
      </>
    </ScrollingJob>
  );
};
